import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {
    SEO_LIMITS,
    TWITTER_CARDS,
    deleteSongSeo,
    getSongSeo,
    saveSongSeo,
} from '../../../../_services/creater/songContent.service';

// Every control here carries one of these. The label says WHAT the field is in
// two words; this says what it does, what happens when it is left empty, and
// whether it is worth setting at all — which for half of them is "no".
const FieldInfo = ({ text, sx }) => (
    <Tooltip title={text} arrow>
        <InfoIcon sx={{ color: 'action.active', fontSize: '15px', cursor: 'help', ...sx }} />
    </Tooltip>
);

// The stored columns, in the order they are edited. Everything the API accepts
// appears here — a field the form cannot reach is a field the creator can only
// change through the Django admin.
const TEXT_FIELDS = [
    {
        name: 'meta_title',
        label: 'Meta title',
        help: 'Shown as the headline in search results.',
        info: 'The blue clickable headline in a Google result. Google shows roughly '
            + '60 characters and cuts off the rest, which is why the counter turns red '
            + 'there. Leave it empty and the page uses the song title and artist name.',
    },
    {
        name: 'meta_description',
        label: 'Meta description',
        help: 'The grey text under the headline in search results.',
        multiline: true,
        rows: 2,
        info: 'The grey summary under the headline in a Google result, about 160 '
            + 'characters. It does not change where you rank — it is what persuades '
            + 'someone to click. Leave it empty and the About text is used.',
    },
    {
        name: 'meta_keywords',
        label: 'Meta keywords',
        help: 'Comma separated.',
        info: 'A comma-separated list of search terms. Google has ignored this tag '
            + 'since 2009 — it is here only because some smaller search engines and '
            + 'internal tools still read it. Safe to leave empty.',
    },
    {
        name: 'canonical_url',
        label: 'Canonical URL',
        help: 'Set this only when the same song is published at another address.',
        info: 'The address you consider this song\u2019s real home. Set it only when the '
            + 'same song is also published somewhere else: it tells search engines which '
            + 'copy to rank and stops the two competing with each other. Almost every '
            + 'song should leave this empty.',
    },
    {
        name: 'og_title',
        label: 'Social title',
        help: 'Falls back to the meta title when empty.',
        info: 'The headline in the preview card people see when your link is pasted '
            + 'into WhatsApp, Instagram, Facebook or X. Leave it empty to reuse the '
            + 'meta title.',
    },
    {
        name: 'og_description',
        label: 'Social description',
        help: 'Falls back to the meta description when empty.',
        multiline: true,
        rows: 2,
        info: 'The line of text under the headline in that shared preview card. Leave '
            + 'it empty to reuse the meta description.',
    },
    {
        name: 'og_image_url',
        label: 'Social image URL',
        help: 'Falls back to the song poster when empty.',
        info: 'The picture in the preview card when someone shares this song. Leave it '
            + 'empty and the song\u2019s own poster is used \u2014 worth setting only when the '
            + 'cover art does not read well as a wide banner, for example a version with '
            + 'the title written on it.',
    },
];

const TWITTER_CARD_INFO =
    'How large the picture is in a shared link preview. Large image is a full-width '
    + 'banner with the title underneath, which is the right choice for music. Small '
    + 'summary is a thumbnail beside the text, the way a news article appears.';

const NO_INDEX_INFO =
    'Keeps this song out of Google and Bing results. The page still works and still '
    + 'shows a preview when shared \u2014 it just cannot be found by searching. Use it for '
    + 'an unlisted track or a pre-release you are only sharing directly.';

const NO_FOLLOW_INFO =
    'Tells search engines not to pass ranking credit through the streaming links on '
    + 'this page. It hides nothing and stops nobody clicking. Genuinely niche \u2014 leave '
    + 'it off unless someone doing SEO work has asked for it.';

const EMPTY = {
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    canonical_url: '',
    og_title: '',
    og_description: '',
    og_image_url: '',
    twitter_card: 'summary_large_image',
    no_index: false,
    no_follow: false,
};

// The backend rejects an over-long title or description outright, so the same
// limit is enforced here — a counter that turns red while typing is a better
// answer than a 400 after pressing save.
const overLimit = (name, value) =>
    Boolean(SEO_LIMITS[name] && String(value || '').length > SEO_LIMITS[name]);

/**
 * Per-song SEO overrides.
 *
 * Every field is optional, and that is the point: with no row stored at all the
 * song page derives its title from the song and artist and its description from
 * the About text. This screen exists for the songs where that derived answer is
 * not good enough — so "empty" is a valid, common and non-alarming state, and
 * `Reset` deletes the row rather than saving blanks over it.
 */
export default function SongSeoFields({ songId, disabled }) {
    const [form, setForm] = useState(EMPTY);
    const [stored, setStored] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');

    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (!songId) return;
        let cancelled = false;
        setLoading(true);
        getSongSeo(songId).then((response) => {
            if (cancelled) return;
            const rows = (response && response.data && response.data.data) || [];
            // `id` is the row's own key and is not writable; keeping it out of
            // the form means the save payload is exactly the editable columns.
            const { id, ...row } = rows[0] || {};
            setStored(rows.length > 0);
            setForm({ ...EMPTY, ...Object.keys(row).reduce((acc, key) => {
                acc[key] = row[key] === null ? EMPTY[key] : row[key];
                return acc;
            }, {}) });
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [songId]);

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setNotice('');
        setError('');
    };

    const blocked = TEXT_FIELDS.some((field) => overLimit(field.name, form[field.name]));

    const handleSave = async () => {
        if (blocked) return;
        setSaving(true);
        setNotice('');
        setError('');
        const response = await saveSongSeo(songId, form);
        if (!mounted.current) return;
        setSaving(false);

        if (response && (response.status === 200 || response.status === 201)) {
            setStored(true);
            setNotice('Search settings saved.');
            return;
        }
        const data = (response && response.data) || {};
        // DRF answers a field error as `{ field: ["..."] }` and an ownership
        // failure as `{ message: "..." }`. Both are shown as written rather than
        // replaced with a generic line, because both say something useful.
        const fieldError = Object.keys(data)
            .filter((key) => Array.isArray(data[key]))
            .map((key) => data[key][0])[0];
        setError(fieldError || data.message || 'Those settings could not be saved.');
    };

    const handleReset = async () => {
        setSaving(true);
        setNotice('');
        setError('');
        const response = await deleteSongSeo(songId);
        if (!mounted.current) return;
        setSaving(false);
        if (response && (response.status === 204 || response.status === 200)) {
            setForm(EMPTY);
            setStored(false);
            setNotice('Cleared — this song is back to its automatic search listing.');
            return;
        }
        setError(((response && response.data) || {}).message || 'That could not be cleared.');
    };

    if (loading) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}><Skeleton variant="rounded" height={40} /></Grid>
                <Grid item xs={12}><Skeleton variant="rounded" height={64} /></Grid>
            </Grid>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    Search &amp; social
                </Typography>
                <Chip
                    size="small"
                    label={stored ? 'Custom' : 'Automatic'}
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
                    color={stored ? 'primary' : 'default'}
                    variant={stored ? 'filled' : 'outlined'}
                />
            </div>
            <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 2 }}>
                Leave these empty and the song page writes its own listing from the title,
                artist and About text. Fill one in to override it.
            </Typography>

            <Grid container spacing={2}>
                {TEXT_FIELDS.map((field) => {
                    const limit = SEO_LIMITS[field.name];
                    const length = String(form[field.name] || '').length;
                    const tooLong = overLimit(field.name, form[field.name]);
                    return (
                        // Full width, every one of them. At md={6} the single-line
                        // fields sat two to a row, which put a URL field and a
                        // title field side by side at half the panel each —
                        // narrow enough that a canonical URL scrolled inside its
                        // own input on the widest card in the app.
                        <Grid item xs={12} key={field.name}>
                            <TextField
                                className="w-100"
                                size="small"
                                id={`seo_${field.name}`}
                                name={field.name}
                                label={field.label}
                                value={form[field.name] || ''}
                                onChange={(event) => setField(field.name, event.target.value)}
                                multiline={field.multiline}
                                rows={field.rows}
                                disabled={disabled || saving}
                                error={tooLong}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            // A multiline input grows downward and an
                                            // adornment centred against three rows of
                                            // text floats in the middle of the box; on
                                            // those it is pinned to the first line so
                                            // every icon in the form sits at the same
                                            // height as its label.
                                            sx={field.multiline
                                                ? { alignSelf: 'flex-start', mt: 1 }
                                                : undefined}
                                        >
                                            <FieldInfo text={field.info} />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText={
                                    limit
                                        // The count is the helper text when there is a
                                        // limit: two lines under one small input is more
                                        // chrome than either message is worth.
                                        ? `${field.help} ${length}/${limit}`
                                        : field.help
                                }
                            />
                        </Grid>
                    );
                })}

                <Grid item xs={12} md={6}>
                    {/* Outside the control, not inside it: a Select already owns its
                        end adornment for the dropdown arrow, and a second icon there
                        reads as a second control. */}
                    <div className="d-flex align-items-center gap-2">
                        <FormControl fullWidth size="small" disabled={disabled || saving}>
                            <InputLabel id={`seo_twitter_card_${songId}`}>Social card size</InputLabel>
                            <Select
                                labelId={`seo_twitter_card_${songId}`}
                                label="Social card size"
                                value={form.twitter_card || 'summary_large_image'}
                                onChange={(event) => setField('twitter_card', event.target.value)}
                            >
                                {TWITTER_CARDS.map((card) => (
                                    <MenuItem key={card} value={card}>
                                        {card === 'summary_large_image' ? 'Large image' : 'Small summary'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FieldInfo text={TWITTER_CARD_INFO} sx={{ flexShrink: 0 }} />
                    </div>
                </Grid>

                <Grid item xs={12} md={6}>
                    {/* The icon rides INSIDE the label node so it stays part of the
                        clickable label rather than becoming a sibling the switch's
                        hit area does not cover. */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(form.no_index)}
                                onChange={(event) => setField('no_index', event.target.checked)}
                                disabled={disabled || saving}
                            />
                        }
                        label={
                            <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                                Hide from search engines
                                <FieldInfo text={NO_INDEX_INFO} />
                            </span>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={Boolean(form.no_follow)}
                                onChange={(event) => setField('no_follow', event.target.checked)}
                                disabled={disabled || saving}
                            />
                        }
                        label={
                            <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                                Do not follow links
                                <FieldInfo text={NO_FOLLOW_INFO} />
                            </span>
                        }
                    />
                </Grid>
            </Grid>

            {error && (
                <Alert severity="warning" className="mt-2" sx={{ fontSize: 13, py: 0.25 }}>
                    {error}
                </Alert>
            )}
            {notice && (
                <Alert severity="success" className="mt-2" sx={{ fontSize: 13, py: 0.25 }}>
                    {notice}
                </Alert>
            )}

            <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className="gn-actionbtn"
                    style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                    disabled={disabled || saving || blocked}
                    onClick={handleSave}
                >
                    {saving ? 'Saving…' : 'Save search settings'}
                </button>
                {stored && (
                    <button
                        type="button"
                        className="gn-actionbtn outlined dark"
                        style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                        disabled={disabled || saving}
                        onClick={handleReset}
                    >
                        Reset to automatic
                    </button>
                )}
            </div>
        </div>
    );
}
